import React, { useRef, useEffect, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
} from "chart.js";
import { useInView, motion, animate } from "framer-motion";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip);

const INK = "#0A0A0A";
const BRAND = "#055FFA"; // Byte Blue
const CYAN = "#66F0FF"; // Cloud Cyan
const GREEN = "#28D55C"; // Matrix Green
const PALE = "#BFEFFF";
const PALETTE = [BRAND, "#000000", CYAN, GREEN, "#D6E4FF"];

// ── value label plugin: draws "NN%" at the end of each horizontal bar ──
const valueLabelPlugin = {
  id: "valueLabel",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.type !== "bar") return;
    ctx.save();
    ctx.font = "600 20px 'Brockmann', sans-serif";
    ctx.fillStyle = INK;
    ctx.textBaseline = "middle";
    meta.data.forEach((bar, i) => {
      const v = chart.data.datasets[0].data[i];
      ctx.fillText(`${v}%`, bar.x + 10, bar.y);
    });
    ctx.restore();
  },
};

// ── Horizontal bar chart (Chart.js), animates when scrolled into view ──
export function HBarChart({ labels, values, height = 300, testId = "hbar-chart" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: BRAND,
        hoverBackgroundColor: "#0447C9",
        borderRadius: 0,
        barThickness: 34,
        categoryPercentage: 0.9,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1100,
      easing: "easeOutQuart",
      delay: (ctx) => (ctx.type === "data" ? ctx.dataIndex * 170 : 0),
    },
    layout: { padding: { right: 64 } },
    scales: {
      x: { display: false, max: Math.max(...values) * 1.15, grid: { display: false } },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: INK,
          font: { family: "'Inter', sans-serif", size: 14, weight: 500 },
          crossAlign: "far",
          autoSkip: false,
          callback(value) {
            const label = this.getLabelForValue(value);
            if (label.length <= 26) return label;
            const words = label.split(" ");
            const lines = [];
            let cur = "";
            words.forEach((w) => {
              if ((cur + " " + w).trim().length > 26) {
                lines.push(cur.trim());
                cur = w;
              } else {
                cur = (cur + " " + w).trim();
              }
            });
            if (cur) lines.push(cur);
            return lines;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        backgroundColor: INK,
        titleFont: { family: "'JetBrains Mono', monospace", size: 11 },
        bodyFont: { family: "'Inter', sans-serif", size: 13 },
        callbacks: { label: (c) => ` ${c.raw}%` },
        displayColors: false,
        cornerRadius: 0,
        padding: 10,
      },
      legend: { display: false },
    },
  };

  return (
    <div ref={ref} style={{ height }} data-testid={testId}>
      {inView && <Bar data={data} options={options} plugins={[valueLabelPlugin]} />}
    </div>
  );
}

// ── Doughnut chart with custom legend list ──
export function DonutChart({ labels, values, testId = "donut-chart" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: PALETTE.slice(0, values.length),
        borderColor: "#ffffff",
        borderWidth: 3,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "58%",
    animation: {
      duration: 1600,
      easing: "easeOutQuart",
      animateRotate: true,
      animateScale: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: INK,
        bodyFont: { family: "'Inter', sans-serif", size: 13 },
        callbacks: { label: (c) => ` ${c.label}: ${c.raw}%` },
        displayColors: false,
        cornerRadius: 0,
        padding: 10,
      },
    },
  };

  return (
    <div ref={ref} className="grid md:grid-cols-2 gap-8 items-center" data-testid={testId}>
      <motion.div
        style={{ height: 300 }}
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.4 }}
      >
        {inView && <Doughnut data={data} options={options} />}
      </motion.div>
      <ul className="space-y-3">
        {labels.map((l, i) => (
          <motion.li
            key={l}
            initial={{ opacity: 0, x: 16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.35 + i * 0.12, duration: 0.5 }}
            className="flex items-center gap-3 text-[15px] text-[#0A0A0A]"
          >
            <span className="w-3.5 h-3.5 shrink-0" style={{ background: PALETTE[i] }} />
            <span className="font-display font-semibold text-[20px] w-14">{values[i]}%</span>
            <span className="text-[#3D3D3B]">{l}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

// ── Coping bars: selectable % tabs, selected bar turns green ──
const COPED_GREEN = "#43CF73";
const COPED_TEXT_GREEN = "#00B843";

export function CopingBars({ labels, values, widths, source, bare = false, testId = "coping-bars" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [sel, setSel] = useState(values.length - 1);
  const tabs = values.map((_, i) => i).reverse();
  const barColor = bare ? "#EEF0FA" : "#F0F4F9";
  const activeColor = bare ? "#5BBF6B" : COPED_GREEN;
  // bar widths proportional to their values (largest bar keeps the reference width)
  const maxVal = Math.max(...values);
  const maxW = Math.max(...(widths || [bare ? 80 : 73.7]));
  const barW = (i) => (values[i] / maxVal) * maxW;

  return (
    <div
      ref={ref}
      className={bare ? "" : "bg-white border border-[#EEF1F4] rounded-[10px] px-4 pt-5 pb-7 md:px-6 md:pt-6 md:pb-9"}
      data-testid={testId}
    >
      {/* percent tabs */}
      <div className="inline-flex mb-8 md:mb-12" role="tablist" data-testid="coping-tabs">
        {tabs.map((bi, ti) => {
          const active = sel === bi;
          return (
            <button
              key={ti}
              role="tab"
              aria-selected={active}
              onClick={() => setSel(bi)}
              className={`w-[56px] md:w-[62px] py-2 md:py-[10px] text-[14px] md:text-[15px] text-center border -ml-px first:ml-0 focus:outline-none ${
                active
                  ? "relative z-10 border-[#DEE3E8] text-[#0A0A0A] font-semibold bg-white"
                  : "border-[#EDF0F4] text-[#8CA0B3] bg-white hover:text-[#5f7488]"
              }`}
              style={{ transition: "color 0.3s, border-color 0.3s" }}
              data-testid={`coping-tab-${ti}`}
            >
              {values[bi]}%
            </button>
          );
        })}
      </div>

      {/* rows */}
      <div className="relative">
        <div className="absolute inset-y-0 right-0 left-[46px] md:left-[56px] pointer-events-none" aria-hidden="true">
          {Array.from({ length: 11 }).map((_, i) => (
            <span key={i} className="absolute inset-y-0 w-px" style={{ left: `${i * 10}%`, background: "rgba(10,10,10,0.1)" }} />
          ))}
        </div>
        <div className={`relative ${bare ? "space-y-[14px]" : "space-y-[10px]"}`}>
          {labels.map((l, i) => {
            const active = sel === i;
            return (
              <div key={l} className="flex items-center gap-[10px]" data-testid={`coping-row-${i}`}>
                <span
                  className={`w-9 md:w-[46px] shrink-0 text-right leading-none ${
                    active ? "text-[16px] md:text-[17px] font-semibold text-[#0A0A0A]" : "text-[14px] md:text-[15px] text-[#9AA7B5]"
                  }`}
                  style={{ transition: "color 0.4s" }}
                >
                  {values[i]}%
                </span>
                <div className="flex-1 min-w-0">
                  <span
                    className="md:hidden block text-[11.5px] leading-snug mb-1.5"
                    style={{ color: active ? "#0A0A0A" : "#7D8288", transition: "color 0.45s" }}
                  >
                    {l}
                  </span>
                  <div className={`relative ${bare ? "h-[38px] md:h-[52px]" : "h-[34px] md:h-[46px]"}`}>
                    <motion.div
                      className="absolute left-0 top-0 h-full"
                      initial={{ width: 0 }}
                      animate={inView ? { width: `${barW(i)}%` } : {}}
                      transition={{ duration: 1.05, delay: i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                      style={{
                        background: active ? activeColor : barColor,
                        transition: "background-color 0.45s",
                      }}
                      data-testid={`coping-bar-${i}`}
                    />
                    <span
                      className="hidden md:flex absolute inset-y-0 right-0 items-center justify-end px-3 md:px-5 text-right leading-snug text-[14.5px] pointer-events-none"
                      style={{ color: active ? "#0A0A0A" : "#7D8288", transition: "color 0.45s" }}
                    >
                      {l}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {source && (
        <p className="mt-8 md:mt-11 pl-[46px] md:pl-[56px] text-[14px] md:text-[15px] text-[#808080]" data-testid="coping-source">
          Source: <span className="font-semibold" style={{ color: COPED_TEXT_GREEN }}>300</span> structured operator interviews
        </p>
      )}
    </div>
  );
}

// ── Blocker rings: concentric arcs sweeping clockwise from ~12 o'clock ──
function LegendCoin({ color }) {
  const rows = [[-13.5, 6.5], [-9, 11], [-4.5, 14], [0, 16.5], [4.5, 14], [9, 11], [13.5, 6.5]];
  return (
    <svg width="34" height="34" viewBox="-17 -17 34 34" className="shrink-0 mt-1" aria-hidden="true">
      {rows.map(([y, rx]) => (
        <ellipse key={y} cx="0" cy={y} rx={rx} ry="1.9" fill={color} />
      ))}
    </svg>
  );
}

export function BlockerRings({ labels, values, testId = "blocker-rings" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const RINGS = [
    { color: "#9DFC98", r: 193 },
    { color: "#53B851", r: 152 },
    { color: "#2E2E2E", r: 111 },
  ];
  const STROKE = 34;
  const CAPX = 40;
  const c = 230;

  return (
    <div ref={ref} className="flex flex-col md:flex-row md:items-center gap-10 md:gap-16" data-testid={testId}>
      <svg viewBox="0 0 460 460" className="w-full max-w-[460px] shrink-0">
        {RINGS.map((ring, i) => {
          const startDeg = -(Math.asin(CAPX / ring.r) * 180) / Math.PI;
          return (
            <g key={i} transform={`rotate(${-90 + startDeg} ${c} ${c})`}>
              <motion.circle
                cx={c}
                cy={c}
                r={ring.r}
                fill="none"
                stroke={ring.color}
                strokeWidth={STROKE}
                strokeLinecap="butt"
                initial={{ pathLength: 0 }}
                animate={inView ? { pathLength: Math.min((values[i] * 2) / 100, 1) } : {}}
                transition={{ duration: 1.4, delay: 0.25 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`blocker-ring-${i}`}
              />
            </g>
          );
        })}
        {RINGS.map((ring, i) => {
          const y = c - Math.sqrt(ring.r * ring.r - CAPX * CAPX);
          return (
            <motion.text
              key={i}
              x={c - CAPX - 16}
              y={y + 7}
              textAnchor="end"
              style={{ fontSize: 21, fontWeight: 600, fill: "#0A0A0A", fontFamily: "'Brockmann', 'Inter', sans-serif" }}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.45 + i * 0.2, duration: 0.5 }}
            >
              {`${values[i]}%`}
            </motion.text>
          );
        })}
      </svg>
      <ul className="space-y-10 md:space-y-14">
        {labels.map((l, i) => (
          <motion.li
            key={l}
            className="flex items-start gap-4"
            initial={{ opacity: 0, x: 16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5 + i * 0.15, duration: 0.55 }}
            data-testid={`blocker-legend-${i}`}
          >
            <LegendCoin color={RINGS[i].color} />
            <span className="text-[15.5px] md:text-[16px] leading-relaxed text-[#4A4A4A] max-w-[300px]">{l}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

// ── Animated count-up number ──
export function CountUp({ value, from = 0, suffix = "", prefix = "", className = "", duration = 1.8, onDone }) {
  const ref = useRef(null);
  const spanRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (inView && !done) {
      const controls = animate(from, value, {
        duration,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => {
          if (spanRef.current) spanRef.current.textContent = Math.round(v).toLocaleString();
        },
        onComplete: () => {
          setDone(true);
          if (onDone) onDone();
        },
      });
      return () => controls.stop();
    }
  }, [inView, value, from, duration, done]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      <span ref={spanRef}>0</span>
      {suffix}
    </span>
  );
}

// ── Chart block wrapper: title + rule + source line ──
export function ChartBlock({ title, subtitle, source, children, testId = "chart-block" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="my-12 md:my-16"
      data-testid={testId}
    >
      <figcaption className="pb-4 border-b border-[#0A0A0A] mb-8">
        <span className="font-display font-semibold text-[18px] md:text-[20px] text-[#0A0A0A] block">{title}</span>
        {subtitle && <span className="text-[13.5px] text-[#6B6B67] leading-relaxed block mt-1.5">{subtitle}</span>}
      </figcaption>
      {children}
      {source && (
        <p className="font-mono text-[11px] text-[#8A8A86] mt-6 tracking-wide">{source}</p>
      )}
    </motion.figure>
  );
}
